import capitalize from 'lodash/capitalize';
import sanitizeHtml from 'sanitize-html';

export function doubleEscapeSpecialChars (string) {
    return string.replace(/\n/g, '\\n').replace(/\t/g, '\\t');
}

export function escapeAllBackslashes (string) {
    return string.replace(/\\/g, '\\\\');
}

export function numberAsStringWithCommas (num) {
    return num.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
}

export function formatTextWithCommas (options, conjunction = 'and') {
    if (Array.isArray(options) && options.length) {
        return options.reduce((acc, next, idx, src) => {
            const sep = src.length === 2 ? '' : ',';

            return idx === src.length - 1 ? `${acc}${sep} ${conjunction} ${next}` : `${acc}${sep} ${next}`;
        });
    }

    return '';
}

export function middleTruncation (str, start, end) {
    const seperator = '...';

    return str.substr(0, start) + seperator + str.substr(str.length - end);
}

export function trimLiteral (templateLiteral) {
    return templateLiteral.replace(/(\n(\s\s)+)/g, ' ').trim();
}

export function doubleEncodeUri (uri) {
    return encodeURIComponent(encodeURIComponent(uri));
}

export function doubleDecodeUri (uri) {
    try {
        uri = decodeURIComponent(uri);
        uri = decodeURIComponent(uri);

        return uri;
    } catch {
        return uri;
    }
}

/**
 * Converts a camel-cased string into a sentence.
 *
 * Converted from legacy, proprietary underscore.js mixin.
 *
 * @example sentencize('helloFromTheOtherSide') === 'Hello from the other side'
 * @deprecated Use i18n utilities instead.
 */
function sentencize (str) {
    if (typeof str === 'string') {
        return capitalize(str.replace(/[a-z][A-Z]/g, (match) => `${match[0]} ${match[1]}`));
    }

    return str;
}

export function capitalizeEachWord (str = '') {
    return str
        ? str
            .split(' ')
            .map((s) => `${s.charAt(0).toUpperCase()}${s.slice(1)}`)
            .join(' ')
        : '';
}

/**
 * Converts a camel-cased string into a title-cased sentence.
 *
 * Converted from legacy, proprietary underscore.js mixin.
 *
 * @example titlecase('helloFromTheOtherSide') === 'Hello From The Other Side'
 * @deprecated Use i18n utilities instead.
 */
export function titlecase (str) {
    if (typeof str === 'string') {
        return capitalizeEachWord(sentencize(str));
    }

    return str;
}

/**
 * @param {string} str
 * @returns returns a pascal case string ThisIsPascalCase.
 */
export function pascalCase (str = '') {
    return str
        ? str
            .split(' ')
            .map((s) => `${s.charAt(0).toUpperCase()}${s.slice(1).toLowerCase()}`)
            .join('')
        : '';
}

/**
 * @param {string} Id
 * @returns encode URI component version of the Id string by encoding special URL characters.
 */
export const encodeSpecialChars = (id) => {
    const specialURLChars = /[/?$&#+=%:;'"<>|]/gi;

    return id.replace(specialURLChars, (match) => encodeURIComponent(match));
};

export function splitStringByNewline (str = '') {
    return str.split('\n');
}

/**
 * Generates a simple, non-cryptographic hash code for a given string.
 * This function is designed for general-purpose hashing where strict 32-bit integer
 * truncation via bitwise operations is not allowed by a linter, meaning the
 * returned hash will be a standard JavaScript number (double-precision float)
 * and can exceed the 32-bit integer range.
 *
 * The algorithm used is a variation of the djb2 or sdbm hash function,
 * which is quick and generally produces good distribution for short to medium strings.
 *
 * @param {string} [str=''] - The input string to hash. Defaults to an empty string.
 * @returns {number} The calculated hash code as a standard JavaScript number.
 * Returns 0 for an empty input string.
 */
export const hashCode = (str = '') => {
    if (str.length === 0) {
        return 0;
    }

    let hash = 0;
    for (let i = 0; i < str.length; i++) {
        const char = str.charCodeAt(i);
        hash = hash * 31 + char;
    }

    return hash;
};

/**
 * Escapes special characters in a string to make it safe for use in a regular expression.
 * @param {string} string
 * @returns properly escaped string for use in a regular expression
 */
function escapeRegExp (string) {
    return string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

/**
 * Takes a string and highlights all instances of searchText by wrapping them in a
 * span with the class 'highlight-text'.
 * @param {string} searchText string used to search and highlight within the text
 * @param {string} text main text body we are searching to highlight
 * @returns {string} the original text with all instances of searchText wrapped in a span with the class 'highlight-text'.
 */
export function generateHighlightTemplate (searchText, text) {
    const regex = new RegExp(escapeRegExp(searchText), 'gi');

    text = sanitizeHtml(text, { allowedTags: [], disallowedTagsMode: 'escape' });

    if (searchText && text.toLocaleLowerCase().includes(searchText.toLocaleLowerCase())) {
        return text.replaceAll(regex, (string) => {
            return `<span class='highlight-text'>${string}</span>`;
        });
    }

    // If no searchText or no matches, return the original text
    return text;
}
